import { useState } from "react";
import { Link as RouterLink } from "react-router";
import {
  Box,
  TextField,
  Button,
  Typography,
  Container,
  Grid,
  InputAdornment,
  IconButton,
  Link,
  Alert,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useNavigate } from "react-router";
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
} from "firebase/auth";
import { auth } from "../../firebase";
import { useCreateUser } from "./createuser.ts";
import { getJaDisplayName } from "components/userUtil.ts";

export default function Register() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const { createUser } = useCreateUser();

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const confirmMessage = `この内容で登録を行います。よろしいですか？`;
    if (!confirm(confirmMessage)) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );
      const user = userCredential.user;
      await sendEmailVerification(user);
      await createUser(companyName);
      navigate("/register/complete");
    } catch (error: any) {
      // エラーハンドリング
      let errorMessage = "登録中にエラーが発生しました。";
      if (error.code === "auth/email-already-in-use") {
        errorMessage = "このメールアドレスは既に使用されています。";
      } else if (error.code === "auth/invalid-email") {
        errorMessage = "無効なメールアドレスです。";
      } else if (error.code === "auth/weak-password") {
        errorMessage =
          "パスワードが弱すぎます。より強力なパスワードを設定してください。";
      }
      setError(errorMessage);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };


  return (
    <Container
      component="main"
      maxWidth="md"
      sx={{
        mt: 8,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      {/* ローディング中のバックドロップ */}
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
        open={loading}
      >
        <CircularProgress color="inherit" />
        <Typography variant="h6">
          処理中です。しばらくお待ちください...
        </Typography>
      </Backdrop>

      <Typography component="h1" variant="h5" sx={{ mb: 1 }}>
        会員登録
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        下記項目ご入力の上、登録をお願いします
      </Typography>
      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={12}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="companyName"
              label={getJaDisplayName("companyName")}
              name="companyName"
              autoComplete="organization"
              helperText="屋号がない場合は個人名をご記入ください"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              disabled={loading}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              margin="normal"
              required
              fullWidth
              autoFocus
              id="email"
              label={getJaDisplayName("email")}
              name="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label={getJaDisplayName("password")}
              helperText="パスワードは6文字以上である必要があります"
              type={showPassword ? "text" : "password"}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        disabled={loading}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Grid>
          {error && (
            <Grid size={12}>
              <Alert severity="error" sx={{ mt: 3, mb: 2 }}>
                {error}
              </Alert>
            </Grid>
          )}
          <Grid size={12}>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" sx={{ my: 0.5 }} />
              ) : (
                "会員登録"
              )}
            </Button>
          </Grid>
        </Grid>
        <Box sx={{ textAlign: "center" }}>
          <Link
            component={RouterLink}
            variant="body2"
            to="/login"
            sx={{
              cursor: loading ? "default" : "pointer",
              pointerEvents: loading ? "none" : "auto",
              color: loading ? "text.disabled" : "primary.main",
            }}
          >
            アカウントをお持ちの方はこちら
          </Link>
        </Box>
      </Box>

    </Container>
  );
}
